import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Input,
    Typography,
} from "@material-tailwind/react";

import {
    XMarkIcon,
    CalendarDaysIcon,
} from "@heroicons/react/24/solid";

import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { assignAppointment, getSalesManagers } from "@/store/slices/appointmentSlice";
import { useDispatch, useSelector } from "react-redux";
import Toaster from "@/components/Toaster";
import { fetchSalesManager, fetchSalesTeamMember, getSalesMemberById } from "@/store/slices/rfqSlice";
import { useEffect } from "react";
import moment from "moment";
import ShowLoader from "@/components/Loader";
import { useTranslation } from "react-i18next";

const customSelectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: "44px",
        borderRadius: "8px",
        borderColor: "#E5E7EB",
        boxShadow: "none",
    }),
};

// const priorities = [
//     { label: "High", value: "high" },
//     { label: "Medium", value: "medium" },
//     { label: "Low", value: "low" },
// ];

export default function AssignAppointmentModal({
    open,
    onClose,
    appointment,
    onSubmited
}) {

    const dispatch = useDispatch();
    const { salesManagers } = useSelector((state) => state.appointment);

    const { t } = useTranslation('appointment');
    const { t: ct } = useTranslation("common");

    const priorities = [
        { label: t("high"), value: "high" },
        { label: t("medium"), value: "medium" },
        { label: t("low"), value: "low" },
    ];

    useEffect(() => {
        dispatch(getSalesManagers());
    }, [dispatch]);


    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            meeting_location: "office",
            assignment_note: "",
            outside_location: "",
            manager_offices: [],
            manager_id:null,
        },
    });

    const meetingLocation = watch("meeting_location");

    const {
        loading,
    } = useSelector(
        (state) => state.appointment
    );

    useEffect(() => {
        if (appointment) {
            setValue(
                "appointment_datetime",
                `${appointment.appointment_date}T${appointment.appointment_time.slice(0, 5)}`
            );
        }
    }, [appointment, setValue]);


    const onSubmit = async (data) => {

        const payload = {
            id: appointment?.appointment_id,
            manager_id: data.manager_id,
            office_id: data.office_id?.value || null,
            meeting_location: data.meeting_location,
                outside_location:
                    data.meeting_location === "outside"
                        ? data.outside_location
                        : null,
            priority: data.priority?.value,
            assignment_note: data.assignment_note,
            appointment_date: appointment.appointment_date,
            appointment_time: appointment.appointment_time,
        };

        try {
            const response = await dispatch(
                assignAppointment?.(payload)
            ).unwrap();

            if (response?.status) {
                Toaster.success(response?.message ||  t("assignSuccess"));
                reset();
                onSubmited();
            } else {
                Toaster.error(response?.message || t("assignFailed"));
            }

        } catch (error) {
            Toaster.error(error?.message || t("assignFailed"));
        }
    };

    const close = () => {
        reset();
        onClose();
    }

    return (
        <Dialog
            open={open}
            handler={close}
            size="sm"
            className="rounded-xl p-5"
        >
            {loading && (<ShowLoader />)}

            <DialogHeader className="border-b flex justify-between items-center">
                <Typography
                    variant="h4"
                    className="font-semibold text-black"
                >
                    {t("title2")}
                </Typography>

                <XMarkIcon
                    onClick={close}
                    className="h-6 w-6 cursor-pointer text-gray-700"
                />
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogBody className="max-h-[70vh] overflow-y-auto">
                    <div className="space-y-5">

                        {/* Sales Manager */}
                        <div>
                            <Typography
                                variant="small"
                                className="mb-2 font-medium"
                            >
                                {t("selectManagerLabel")}
                            </Typography>

                            <Controller
                                control={control}
                                name="manager_id"
                                rules={{
                                    required:
                                        t("selectManagerError"),
                                }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={salesManagers || []}
                                        placeholder={t("selectManagerPlaceholder")}
                                        styles={customSelectStyles}
                                        value={salesManagers?.find(row => row?.value === watch('manager_id'))}
                                        // onChange={(selectedOption) => {
                                        //     setValue("manager_id", selectedOption?.value, { shouldValidate: true });
                                        //     setValue("manager_offices", selectedOption.offices || []);
                                        // }}
                                        onChange={(selectedOption) => {
                                        setValue("manager_id", selectedOption?.value, { shouldValidate: true });
                                        setValue(
                                            "manager_offices",
                                            (selectedOption?.offices || []).map((office) => ({
                                                ...office,
                                                label: office.label?.en || "",
                                            }))
                                        );
                                    }}
                                    />
                                )}
                            />

                            {errors.manager_id && (
                                <p className="mt-1 text-sm text-red-500">
                                    {errors.manager_id.message}
                                </p>
                            )}
                        </div>
                        {/* Sales Member */}
                        {/* <div>
                            <Typography
                                variant="small"
                                className="mb-2 font-medium"
                            >
                                Select Sales Member
                            </Typography>

                            <Controller
                                control={control}
                                name="sales_rep_id"
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={sales_team}
                                        placeholder="Select Member"
                                        styles={customSelectStyles}
                                    />
                                )}
                            />

                            {errors.sales_rep_id && (
                                <p className="mt-1 text-sm text-red-500">
                                    {errors.sales_rep_id.message}
                                </p>
                            )}
                        </div> */}

                        {/* Meeting Location */}
                        <div>
                            <Typography
                                variant="small"
                                className="mb-3 font-medium"
                            >
                                {t("meetingLocationLabel")}
                            </Typography>

                            <div className="flex gap-8">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        value="office"
                                        {...register(
                                            "meeting_location"
                                        )}
                                    />
                                    <span className="text-sm">
                                        {t("respectiveOfficeLabel")}
                                    </span>
                                </label>

                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        value="outside"
                                        {...register(
                                            "meeting_location"
                                        )}
                                    />
                                    <span className="text-sm">
                                        {t("outsideLabel")}
                                    </span>
                                </label>
                            </div>
                            {meetingLocation === "outside" && (
                                <div>
                                    <Typography
                                        variant="small"
                                        className="mb-2 mt-3 font-medium"
                                    >
                                        {t("outsideMeetingLocation")}
                                         <span className="text-red-500">*</span>
                                    </Typography>

                                    <Input
                                        type="text"
                                        placeholder={t("enterOutsideMeetingLocation")}
                                        {...register("outside_location", {
                                            required:
                                                meetingLocation === "outside"
                                                    ? t("outsideMeetingLocationRequired")
                                                    : false,
                                            minLength: {
                                                value: 3,
                                                message:
                                                    "Location must be at least 3 characters.",
                                            },
                                            maxLength: {
                                                value: 250,
                                                message:
                                                    "Location must not exceed 250 characters.",
                                            },
                                            validate: (value) =>
                                                value.trim().length >= 3 ||
                                                "Location must be at least 3 characters.",
                                        })}
                                        className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                                        labelProps={{
                                            className:
                                                "before:content-none after:content-none",
                                        }}
                                    />

                                    {errors.outside_location && (
                                        <p className="mt-1 text-sm text-red-500">
                                            {errors.outside_location.message}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Office */}
                        {meetingLocation === "office" && (
                            <div>
                                <Typography
                                    variant="small"
                                    className="mb-2 font-medium"
                                >
                                    {t("selectOfficeLabel")}
                                </Typography>

                                <Controller
                                    control={control}
                                    name="office_id"
                                    rules={{
                                        required:
                                            meetingLocation === "office"
                                                ? t("selectOfficeError")
                                                : false,
                                    }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={watch("manager_offices") || []}
                                            placeholder={t("selectOfficeLabel")}
                                            styles={customSelectStyles}
                                        />
                                    )}
                                />

                                {errors.office_id && (
                                    <p className="mt-1 text-sm text-red-500">
                                        {errors.office_id.message}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Appointment Date & Time + Priority */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Typography
                                    variant="small"
                                    className="mb-2 font-medium"
                                >
                                    {t("appointmentDateTimeLabel")}
                                </Typography>

                                <Input
                                    type="datetime-local"
                                    disabled
                                    {...register(
                                        "appointment_datetime",
                                        {
                                            required: t("appointmentDateTimeRequired"),
                                            validate: (
                                                value
                                            ) => {
                                                const selectedDate =
                                                    new Date(
                                                        value
                                                    );
                                                const now =
                                                    new Date();

                                                return (
                                                    selectedDate >
                                                    now ||
                                                    t("appointmentDateTimeFuture")
                                                );
                                            },
                                        }
                                    )}
                                    className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{
                                        className:
                                            "before:content-none after:content-none",
                                    }}
                                />

                                {errors.appointment_datetime && (
                                    <p className="mt-1 text-sm text-red-500">
                                        {
                                            errors
                                                .appointment_datetime
                                                .message
                                        }
                                    </p>
                                )}
                            </div>

                            <div>
                                <Typography
                                    variant="small"
                                    className="mb-2 font-medium"
                                >
                                    {t("priorityLabel")}
                                </Typography>

                                <Controller
                                    control={control}
                                    name="priority"
                                    rules={{
                                        required:
                                            t("priorityError"),
                                    }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={priorities}
                                            placeholder={t("selectPriorityPlaceholder")}
                                            styles={customSelectStyles}
                                        />
                                    )}
                                />

                                {errors.priority && (
                                    <p className="mt-1 text-sm text-red-500">
                                        {
                                            errors.priority
                                                .message
                                        }
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Note */}
                        <div>
                            <Typography
                                variant="small"
                                className="mb-2 font-medium"
                            >
                               {t("noteLabel")}
                            </Typography>

                            <textarea
                                rows={4}
                                placeholder={t('notePlaceholder')}
                                className="w-full rounded-lg border border-gray-300 px-4 py-3 resize-none focus:outline-none focus:border-black"
                                {...register(
                                    "assignment_note",
                                    {
                                        required: t("noteRequired"),
                                        minLength: {
                                            value: 10,
                                            message:
                                                t("noteMinLength"),
                                        },
                                        maxLength: {
                                            value: 500,
                                            message:
                                                t("noteMaxLength"),
                                        },
                                    }
                                )}
                            />

                            {errors.assignment_note && (
                                <p className="mt-1 text-sm text-red-500">
                                    {
                                        errors
                                            .assignment_note
                                            .message
                                    }
                                </p>
                            )}
                        </div>
                    </div>
                </DialogBody>

                <DialogFooter className="border-t flex justify-end gap-3">
                    <Button
                        variant="outlined"
                        color="gray"
                        onClick={close}
                    >
                       {ct("notNowButton")}
                    </Button>

                    <Button
                        type="submit"
                        className="bg-amber-500 shadow-none hover:shadow-none"
                    >
                        {ct("assignButton")}
                    </Button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}